import React, { useState, useEffect, useRef } from "react";
import {
  MapPin,
  Phone,
  Wifi,
  Wind,
  Droplets,
  Tv,
  Coffee,
  Star,
  Menu,
  X,
  BedDouble,
  Users,
  ArrowRight,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ExternalLink,
  Quote,
  LayoutGrid,
  ShieldCheck,
} from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import { toLocalISODate } from "../utils/date";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const BE = process.env.REACT_APP_BACKEND_URL || "";

function imgSrc(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BE}${url}`;
}

const SLIDESHOW_INTERVAL_MS = 3500;

// Auto-advancing crossfade slideshow for a room card's photos — all stacked
// in place and crossfaded via opacity so there's no layout shift. Photo 0
// (the admin-chosen thumbnail, see RoomsPage.jsx) always shows first. A
// single photo just renders statically, and prefers-reduced-motion stops
// the auto-advance entirely, leaving the thumbnail as a still image.
function RoomPhotoSlideshow({ photos, alt }) {
  const [index, setIndex] = useState(0);
  const reduceMotion = useRef(
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  useEffect(() => {
    if (photos.length < 2 || reduceMotion.current) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % photos.length);
    }, SLIDESHOW_INTERVAL_MS);
    return () => clearInterval(id);
  }, [photos.length]);

  if (photos.length <= 1) {
    return (
      <img src={photos[0]} alt={alt} loading="lazy"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
    );
  }

  return (
    <>
      {photos.map((src, i) => (
        <img
          key={src + i}
          src={src}
          alt={i === 0 ? alt : ""}
          loading={i === 0 ? "lazy" : "eager"}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 absolute inset-0"
          style={{ opacity: i === index ? 1 : 0, transition: "opacity 1s ease" }}
        />
      ))}
      <div className="absolute bottom-3 right-3 flex gap-1.5 z-10">
        {photos.map((_, i) => (
          <span key={i} className="rounded-full transition-all"
            style={{ width: i === index ? 14 : 6, height: 6, background: i === index ? "#D9A441" : "rgba(255,255,255,.6)" }} />
        ))}
      </div>
    </>
  );
}

// No hardcoded stock fallback here on purpose — this used to fall back to
// bundled photos of a different, real property (see MIGRATION-NOTES.md).
// An empty gallery until the tenant uploads their own (Settings.heroImages,
// via /api/settings/photos) is a real, honest empty state — never someone
// else's photos standing in for it.

const AMENITIES = [
  { icon: Wifi, label: "High-Speed WiFi", desc: "Fibre broadband" },
  { icon: Wind, label: "Air Conditioning", desc: "All rooms AC" },
  { icon: Droplets, label: "24/7 Hot Water", desc: "Electric geyser" },
  { icon: Tv, label: "Smart TV", desc: "OTT streaming" },
  // Parking deliberately NOT listed here. This block is hardcoded copy shown
  // on EVERY tenant's landing page, so it claimed free parking in a secure
  // compound for properties that may have neither — and parking is exactly
  // the promise a guest arrives having relied on. It belongs to the room, not
  // the property: rooms carry real "2-Wheeler Parking" / "4-Wheeler Parking"
  // amenities the tenant sets themselves (RoomsPage.jsx's AMENITY_OPTIONS),
  // and those are what the room detail page shows.
  { icon: Coffee, label: "Kitchen Access", desc: "Fully equipped" },
];

// No hardcoded "nearby attractions" list — these were real Guwahati-area
// places specific to a different property, meaningless (and actively
// misleading) for a tenant anywhere else. There's no per-tenant version of
// this yet — see MIGRATION-NOTES.md's suggestions list. The whole "nearby"
// section below is hidden until one exists, rather than showing the wrong
// city's attractions to every new tenant.

// No hardcoded FAQ list here — fully tenant-editable now (Faq model,
// /api/faqs CRUD, managed from SettingsPage.jsx). The section below just
// doesn't render until an owner adds their own questions, same pattern as
// gallery/rooms/reviews elsewhere on this page.

// No hardcoded reviews here — these used to be real reviews for a
// different, specific property, shown to every tenant as if they were
// theirs (see MIGRATION-NOTES.md). Real reviews come from `liveReviews`
// below (Review model, /api/reviews/public) or nothing at all until a
// tenant has some — never fabricated testimonials standing in.

function StarRow({ n, size = 14 }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={size}
          className={
            i < n
              ? "text-amber-500 fill-amber-500"
              : "text-stone-200 fill-stone-200"
          }
        />
      ))}
    </div>
  );
}

// Small uppercase eyebrow + underline rule used above every section
// heading — the one repeated motif that ties the page together.
function SectionEyebrow({ children, align = "center" }) {
  return (
    <div className={align === "center" ? "text-center" : "text-left"}>
      <span className="text-amber-600 text-xs font-bold uppercase tracking-[0.25em]">
        {children}
      </span>
      <span
        className={`block w-10 h-[2px] bg-amber-500 mt-3 mb-5 ${align === "center" ? "mx-auto" : ""}`}
      />
    </div>
  );
}

function ReviewLookupModal({ onClose }) {
  const { googleReviewUrl } = useSettings();
  const [bookingRef, setBookingRef] = useState("");
  const [whatsapp, setWhatsapp]     = useState("");
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");

  async function submit() {
    if (!bookingRef.trim() || !whatsapp.trim()) { setError("Both fields are required."); return; }
    setLoading(true); setError("");
    try {
      const res  = await fetch(`${API}/reviews/lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bookingRef, whatsapp }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      window.location.href = `/review/${json.data.token}`;
    } catch (e) { setError(e.message || "Something went wrong."); }
    finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-navy-950/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-7 border border-stone-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-serif font-semibold text-navy-900">Write a Review</h3>
          <button onClick={onClose}><X size={18} className="text-stone-400" /></button>
        </div>
        <p className="text-sm text-stone-500 mb-5">
          Stayed with us? Verify your completed stay to leave a review.
        </p>
        <a
          href={googleReviewUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full
            border border-stone-200 text-navy-900 font-bold text-sm
            hover:border-amber-400 hover:text-amber-700 transition-colors mb-4"
        >
          <ExternalLink size={15} /> Review Us on Google
        </a>
        <div className="flex items-center gap-3 mb-4">
          <span className="flex-1 h-px bg-stone-200" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-stone-400">Or review here</span>
          <span className="flex-1 h-px bg-stone-200" />
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold text-navy-500 uppercase tracking-wider mb-1.5">
              Booking Reference
            </label>
            <input type="text" value={bookingRef} onChange={e => setBookingRef(e.target.value)}
              placeholder="BKG-172..."
              className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm
                focus:outline-none focus:ring-2 focus:ring-amber-400/60 focus:border-amber-400" />
          </div>
          <div>
            <label className="block text-xs font-bold text-navy-500 uppercase tracking-wider mb-1.5">
              WhatsApp Number Used at Booking
            </label>
            <input type="tel" value={whatsapp} onChange={e => setWhatsapp(e.target.value)}
              placeholder="+91 98765 43210"
              className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm
                focus:outline-none focus:ring-2 focus:ring-amber-400/60 focus:border-amber-400" />
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
          <button onClick={submit} disabled={loading}
            className="w-full py-3.5 rounded-full bg-navy-900
              text-white font-bold text-sm hover:bg-navy-800
              shadow-sm transition-all disabled:opacity-60">
            {loading ? "Verifying…" : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const {
    hotelName, address, phone, heroImages: uploadedHero, aboutText, faqs,
    mapsUrl, mapsLat, mapsLng, crossPromoName, crossPromoUrl, logoUrl,
  } = useSettings();
  // A tenant with no explicit Google Maps share link (mapsUrl blank) used
  // to render `<a href="">`, which the browser resolves to the CURRENT
  // page — clicking "View on Google Maps" just reloaded the same tenant
  // site instead of going anywhere. Falls back to a real Maps search URL
  // built the same way the embedded iframe below already derives its own
  // src (lat/lng if set, else the address/hotel name), so the link always
  // goes somewhere real even when a tenant never pasted a share link.
  const MAPS_URL = mapsUrl || `https://www.google.com/maps?q=${
    mapsLat && mapsLng ? `${mapsLat},${mapsLng}` : encodeURIComponent(address || hotelName)
  }`;
  const [menuOpen, setMenuOpen] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [bookingDisabled, setBookingDisabled] = useState(false);
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [reviewIdx, setReviewIdx] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(null);
  const [liveReviews, setLiveReviews] = useState([]);
  const [showReviewLookup, setShowReviewLookup] = useState(false);
  const [showFloatingBar, setShowFloatingBar] = useState(false);
  const [backstoryOpen, setBackstoryOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setShowFloatingBar(window.scrollY > 700);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Uploaded during install (Settings.heroImages) take priority over the
  // bundled Dhriti stock photos — same set backs the photo-grid gallery
  // and its lightbox, since the wizard only collects one batch of photos.
  const galleryItems = uploadedHero?.length
    ? uploadedHero.map((url) => ({ src: imgSrc(url), label: hotelName }))
    : [];

  useEffect(() => {
    fetch(`${API}/rooms/public`)
      .then((r) => {
        if (r.status === 404) { setBookingDisabled(true); return { data: [] }; }
        return r.json();
      })
      .then((j) => setRooms(j.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API}/reviews/public`)
      .then((r) => r.json())
      .then((j) => {
        const mapped = (j.data || []).map((r) => ({
          name: r.guestName,
          rating: r.rating,
          date: new Date(r.createdAt).toLocaleDateString("en-IN", { month: "long", year: "numeric" }),
          avatar: r.guestName.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase(),
          text: r.comment || "Great stay!",
          via: "Verified Guest",
        }));
        setLiveReviews(mapped);
      })
      .catch(() => {});
  }, []);

  const allReviews = liveReviews;

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };
  const nights =
    checkIn && checkOut
      ? Math.max(
          0,
          Math.ceil((new Date(checkOut) - new Date(checkIn)) / 86400000),
        )
      : 0;

  // No hardcoded example rooms — a fresh tenant with no real rooms
  // configured yet used to show 3 fake rooms with someone else's real
  // property photos (see MIGRATION-NOTES.md). A real, honest empty state
  // instead (guarded in the ROOMS section's own render below).
  const displayRooms = rooms;

  const lowestPrice = displayRooms.length
    ? Math.min(...displayRooms.map((r) => r.pricePerNight || Infinity))
    : null;
  const bookHref = `/book${checkIn && checkOut ? `?checkIn=${checkIn}&checkOut=${checkOut}` : ""}`;

  return (
    <div className="font-sans text-navy-900 bg-stone-50 overflow-x-hidden">
      {/* ════════ NAVBAR ════════ */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <a
            href="/"
            className="tap-44 flex items-center gap-2.5 hover:opacity-80 transition-opacity"
          >
            {logoUrl ? (
              <img src={imgSrc(logoUrl)} alt={hotelName}
                className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div
                className="w-10 h-10 rounded-full bg-navy-900
                flex items-center justify-center"
              >
                <BedDouble size={18} className="text-amber-400" />
              </div>
            )}
            <div>
              <p className="font-serif font-semibold text-navy-900 text-base leading-tight">
                {hotelName}
              </p>
              <p className="text-[9px] text-stone-400 uppercase tracking-[0.2em]">
                {address}
              </p>
            </div>
          </a>

          <div className="hidden md:flex items-center gap-6 text-sm font-medium text-navy-600">
            {[
              ["Rooms", "rooms"],
              ["Amenities", "amenities"],
              ["Gallery", "gallery"],
              ["Reviews", "reviews"],
              ["FAQ", "faq"],
              ["Contact", "contact"],
            ].map(([l, id]) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className="hover:text-amber-600 transition-colors relative group"
              >
                {l}
                <span
                  className="absolute -bottom-1.5 left-0 w-0 h-px bg-amber-500
                  group-hover:w-full transition-all duration-300"
                />
              </button>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-4">
            <a
              href="/admin"
              className="text-xs text-stone-400 hover:text-navy-600 transition-colors"
            >
              Admin
            </a>
            <a
              href="/book"
              className="flex items-center gap-1.5 text-sm font-semibold text-white
                bg-navy-900 px-5 py-2.5 rounded-full
                hover:bg-navy-800
                transition-all"
            >
              Book Now <ArrowRight size={14} />
            </a>
          </div>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="tap-44 md:hidden p-2"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-stone-100 px-6 py-4 space-y-2">
            {[
              ["Rooms", "rooms"],
              ["Amenities", "amenities"],
              ["Gallery", "gallery"],
              ["Reviews", "reviews"],
              ["FAQ", "faq"],
              ["Contact", "contact"],
            ].map(([l, id]) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className="block w-full text-left text-sm font-semibold text-navy-700 py-2.5 border-b border-stone-50"
              >
                {l}
              </button>
            ))}
            <a
              href="/book"
              className="block text-center text-sm font-bold text-white
              bg-navy-900 px-5 py-3.5 rounded-full mt-3"
            >
              Book Now
            </a>
          </div>
        )}
      </nav>

      <main>
      {/* ════════ TITLE + QUICK FACTS ════════ */}
      <section className="pt-28 pb-6 px-6 max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
          <div>
            <h1 className="font-serif text-3xl md:text-[2.75rem] font-medium text-navy-900 leading-tight">
              {hotelName}
            </h1>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2 text-sm text-stone-500">
              <span className="flex items-center gap-1">
                <MapPin size={14} className="text-stone-400" /> {address}
              </span>
              {allReviews.length > 0 && (
                <>
                  <span className="text-stone-300">·</span>
                  <span className="flex items-center gap-1 text-navy-900 font-semibold">
                    <Star size={13} className="fill-amber-500 text-amber-500" />
                    {(allReviews.reduce((s, r) => s + (r.rating || 0), 0) / allReviews.length).toFixed(1)}
                  </span>
                  <span>({allReviews.length} review{allReviews.length !== 1 ? "s" : ""})</span>
                </>
              )}
              {!bookingDisabled && (
                <>
                  <span className="text-stone-300">·</span>
                  <span>{displayRooms.length} Room Types</span>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {AMENITIES.slice(0, 4).map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-navy-700
                  bg-navy-50 border border-navy-100 px-3 py-1.5 rounded-full"
              >
                <Icon size={13} /> {label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ PHOTO GALLERY GRID ════════ */}
      {galleryItems.length > 0 ? (
      <section id="gallery" className="px-6 max-w-6xl mx-auto pb-14">
        <div className="relative grid grid-cols-4 grid-rows-2 gap-2 rounded-2xl overflow-hidden h-[320px] md:h-[480px]">
          <div
            onClick={() => setGalleryOpen(0)}
            className="col-span-4 row-span-2 md:col-span-2 md:row-span-2 relative cursor-pointer group overflow-hidden"
          >
            <img
              src={galleryItems[0].src}
              alt={galleryItems[0].label}
              loading="eager"
              fetchpriority="high"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          {galleryItems.slice(1, 5).map((item, i) => (
            <div
              key={i}
              onClick={() => setGalleryOpen(i + 1)}
              className="hidden md:block relative cursor-pointer group overflow-hidden"
            >
              <img
                src={item.src}
                alt={item.label}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              {i === 3 && galleryItems.length > 5 && (
                <div className="absolute inset-0 bg-navy-950/50 flex items-center justify-center text-white font-semibold text-sm">
                  +{galleryItems.length - 5} photos
                </div>
              )}
            </div>
          ))}
        </div>
        <button
          onClick={() => setGalleryOpen(0)}
          className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-navy-700
            border border-stone-300 px-4 py-2.5 rounded-full hover:border-navy-400 transition-colors"
        >
          <LayoutGrid size={15} /> View all {galleryItems.length} photos
        </button>
      </section>
      ) : (
      <section id="gallery" className="px-6 max-w-6xl mx-auto pb-14">
        <div className="rounded-2xl border border-dashed border-stone-300 h-[220px] flex items-center justify-center text-stone-400 text-sm">
          Photos coming soon
        </div>
      </section>
      )}

      {/* ════════ CROSS-PROMOTION AD (optional, hotel-specific) ════════ */}
      {crossPromoName && crossPromoUrl && (
      <section className="relative z-10 pb-14 px-6">
        <div className="max-w-6xl mx-auto">
          <a
            href={crossPromoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group block overflow-hidden rounded-2xl border border-navy-800
              transition-all duration-300"
          >
            <div
              className="bg-navy-950 rounded-2xl
              px-8 py-7 flex flex-col md:flex-row items-center justify-between gap-5"
            >
              {/* Left content */}
              <div className="flex items-center gap-5">
                <div
                  className="w-16 h-16 rounded-full bg-amber-400/10 border border-amber-400/30
                  flex items-center justify-center flex-shrink-0
                  group-hover:bg-amber-400/20 transition-colors duration-300"
                >
                  <span className="text-2xl">✈️</span>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="text-[10px] font-bold uppercase tracking-[0.3em]
                      text-amber-400"
                    >
                      Partner Service
                    </span>
                  </div>
                  <h3 className="font-serif text-white font-medium text-xl md:text-2xl leading-tight">
                    Planning a Tour? Try{" "}
                    <span className="italic text-amber-300">
                      {crossPromoName}
                    </span>
                  </h3>
                  <p className="text-stone-400 text-sm mt-1 max-w-md">
                    Recommended by us — check them out for your trip.
                  </p>
                </div>
              </div>
              {/* CTA */}
              <div className="flex-shrink-0 text-center">
                <div
                  className="inline-flex items-center gap-2 bg-amber-400
                  text-navy-950 font-bold text-sm px-7 py-3.5 rounded-full
                  group-hover:bg-amber-300
                  transition-all duration-300"
                >
                  Learn More →
                </div>
                <p className="text-stone-500 text-[10px] mt-2">
                  {crossPromoUrl.replace(/^https?:\/\//, "")}
                </p>
              </div>
            </div>
          </a>
        </div>
      </section>
      )}

      {/* ════════ MAIN CONTENT (sticky booking sidebar alongside) ════════ */}
      <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-x-12 gap-y-20 items-start pb-20">
        <div className="lg:col-span-2 space-y-20">

          {/* ── BACKSTORY ── */}
          {/* Tenant-editable (Settings.aboutText, /api/settings/profile) —
              no hardcoded narrative here on purpose. An earlier version
              showed a specific property's own story to every tenant (see
              MIGRATION-NOTES.md); the whole section just doesn't render
              until a tenant writes their own. */}
          {aboutText && (
          <section>
            <SectionEyebrow align="left">Our Story</SectionEyebrow>
            <h2 className="font-serif text-3xl font-medium text-navy-900 mb-4">
              Why {hotelName}
            </h2>
            <p className={`text-stone-600 leading-relaxed whitespace-pre-line ${backstoryOpen ? "" : "line-clamp-4"}`}>
              {aboutText}
            </p>
            <button
              onClick={() => setBackstoryOpen((v) => !v)}
              className="mt-3 text-sm font-semibold text-amber-600 hover:text-amber-700 inline-flex items-center gap-1"
            >
              {backstoryOpen ? "Read Less" : "Read More"}
              <ChevronDown size={14} className={`transition-transform ${backstoryOpen ? "rotate-180" : ""}`} />
            </button>
          </section>
          )}

          {/* ── ROOMS ── */}
          <section id="rooms">
            <SectionEyebrow align="left">Accommodation</SectionEyebrow>
            <h2 className="font-serif text-3xl font-medium text-navy-900 mb-1">
              Rooms &amp; Beds
            </h2>
            <p className="text-stone-500 mb-8">
              Thoughtfully designed spaces blending modern comfort with warm hospitality
            </p>
            {bookingDisabled ? (
              <div className="rounded-2xl border border-dashed border-stone-300 py-14 text-center text-stone-400 text-sm px-6">
                Online booking is currently unavailable — please call {phone || "us"} to book directly.
              </div>
            ) : displayRooms.length === 0 && (
              <div className="rounded-2xl border border-dashed border-stone-300 py-14 text-center text-stone-400 text-sm">
                Rooms coming soon
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {displayRooms.map((room) => {
                const slidesSrc = (room.photos || []).map(imgSrc).filter(Boolean);
                return (
                  <div
                    key={room._id}
                    className="bg-white rounded-2xl overflow-hidden border border-stone-200
                      hover:shadow-lg transition-all duration-300 group"
                  >
                    <div className="relative h-52 overflow-hidden">
                      {slidesSrc.length > 0 ? (
                        <RoomPhotoSlideshow photos={slidesSrc} alt={room.name} />
                      ) : (
                        <div
                          className="w-full h-full bg-stone-100
                          flex items-center justify-center"
                        >
                          <BedDouble size={40} className="text-stone-300" />
                        </div>
                      )}
                      <span
                        className="absolute top-3 left-3 text-[10px] font-bold text-white
                        uppercase tracking-wider bg-navy-900/80 backdrop-blur px-2.5 py-1 rounded-full"
                      >
                        {room.type}
                      </span>
                    </div>
                    <div className="p-6">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <a href={room._id && !String(room._id).startsWith("d") ? `/rooms/${room._id}` : "#rooms"}
                          className="tap-44 hover:text-amber-600 transition-colors">
                          <h3 className="font-serif font-medium text-navy-900 text-xl">
                            {room.name}
                          </h3>
                        </a>
                        <p className="text-sm font-bold text-navy-900 whitespace-nowrap pt-0.5">
                          ₹{(room.pricePerNight || 0).toLocaleString("en-IN")}
                          <span className="text-stone-400 font-normal text-xs">/night</span>
                        </p>
                      </div>
                      {room.description && (
                        <p className="text-sm text-stone-500 mb-4 line-clamp-2 leading-relaxed">
                          {room.description}
                        </p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-stone-400 mb-5 pb-5 border-b border-stone-100">
                        <span className="flex items-center gap-1">
                          <Users size={12} /> {room.baseOccupancy || room.capacity} guests
                        </span>
                        {(room.amenities || []).slice(0, 3).map((a) => (
                          <span key={a}>{a}</span>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        {room._id && !String(room._id).startsWith("d") && (
                          <a href={`/rooms/${room._id}`}
                            className="flex items-center justify-center px-4 py-3 rounded-full
                              border border-stone-200 text-navy-600 text-sm font-semibold
                              hover:border-navy-300 hover:text-navy-900 transition-all">
                            Details
                          </a>
                        )}
                        <a
                          href={`/book${checkIn && checkOut ? `?checkIn=${checkIn}&checkOut=${checkOut}&` : "?"}roomId=${room._id}`}
                          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full
                            bg-navy-900 text-white text-sm font-bold
                            hover:bg-navy-800 transition-all"
                        >
                          Book This Room <ArrowRight size={14} />
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* ── AMENITIES ── */}
          <section id="amenities">
            <SectionEyebrow align="left">Comforts</SectionEyebrow>
            <h2 className="font-serif text-3xl font-medium text-navy-900 mb-8">
              Everything You Need
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 divide-y divide-stone-100 sm:divide-y-0">
              {AMENITIES.map(({ icon: Icon, label, desc }) => (
                <div
                  key={label}
                  className="flex items-center gap-4 py-4 sm:border-b sm:border-stone-100"
                >
                  <div
                    className="w-11 h-11 flex-shrink-0 bg-navy-50
                    rounded-full flex items-center justify-center"
                  >
                    <Icon size={18} className="text-navy-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy-900 text-sm">{label}</h3>
                    <p className="text-sm text-stone-500">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ── NEARBY ── */}
          {/* Removed (2026-09-02): used to list real Guwahati attractions
              (Kamakhya Temple, Kaziranga, etc.) for every tenant regardless
              of where their actual property is — actively wrong, not just
              generic filler. No per-tenant "nearby attractions" exists yet
              (see MIGRATION-NOTES.md's suggestions list) — hidden until it
              does, rather than showing the wrong city to every new tenant. */}
        </div>

        {/* ── STICKY BOOKING CARD ── */}
        <aside className="hidden lg:block">
          <div className="sticky top-28 bg-white rounded-2xl border border-stone-200 shadow-lg p-6">
            <p className="text-2xl font-bold text-navy-900">
              {lowestPrice !== null ? (
                <>₹{lowestPrice.toLocaleString("en-IN")}<span className="text-sm font-normal text-stone-400"> /night onwards</span></>
              ) : (
                <span className="text-base font-semibold text-stone-400">Rates coming soon</span>
              )}
            </p>
            <div className="grid grid-cols-2 gap-3 mt-5">
              <div className="border border-stone-200 rounded-xl px-3 py-2.5 bg-stone-50/80">
                <label className="block text-[10px] font-bold text-navy-500 uppercase tracking-wider mb-1">
                  Check-in
                </label>
                <input
                  type="date"
                  value={checkIn}
                  min={toLocalISODate(new Date())}
                  onChange={(e) => setCheckIn(e.target.value)}
                  className="w-full bg-transparent text-navy-900 text-xs font-semibold focus:outline-none"
                />
              </div>
              <div className="border border-stone-200 rounded-xl px-3 py-2.5 bg-stone-50/80">
                <label className="block text-[10px] font-bold text-navy-500 uppercase tracking-wider mb-1">
                  Check-out
                </label>
                <input
                  type="date"
                  value={checkOut}
                  min={checkIn || toLocalISODate(new Date())}
                  onChange={(e) => setCheckOut(e.target.value)}
                  className="w-full bg-transparent text-navy-900 text-xs font-semibold focus:outline-none"
                />
              </div>
            </div>
            <a
              href={bookHref}
              className="mt-4 w-full flex items-center justify-center gap-2 py-3.5 rounded-xl
                bg-navy-900 text-white text-sm font-bold hover:bg-navy-800 transition-all"
            >
              {nights > 0 ? `Check ${nights} Night${nights > 1 ? "s" : ""}` : "Check Availability"}
              <ArrowRight size={14} />
            </a>
            <div className="flex items-center gap-2 text-xs text-stone-400 mt-4">
              <ShieldCheck size={14} className="text-navy-400" /> Book direct — no hidden fees
            </div>
          </div>
        </aside>
      </div>

      {/* ════════ REVIEWS ════════ */}
      <section
        id="reviews"
        className="relative z-10 py-24 bg-white border-y border-stone-200"
      >
        <div className="max-w-6xl mx-auto px-6">
          <SectionEyebrow>Guest Stories</SectionEyebrow>
          <div className="text-center mb-14">
            <h2 className="font-serif text-4xl md:text-5xl font-medium text-navy-900 mb-3">
              What Our Guests Say
            </h2>
            {allReviews.length > 0 ? (
              <div className="flex items-center justify-center gap-2 mt-3">
                <StarRow n={Math.round(allReviews.reduce((s, r) => s + (r.rating || 0), 0) / allReviews.length)} size={16} />
                <span className="text-base font-bold text-navy-900">
                  {(allReviews.reduce((s, r) => s + (r.rating || 0), 0) / allReviews.length).toFixed(1)}
                </span>
                <span className="text-stone-400 text-sm">
                  · {allReviews.length} review{allReviews.length !== 1 ? "s" : ""}
                </span>
              </div>
            ) : (
              <p className="text-stone-400 text-sm mt-3">No reviews yet — be the first to stay and share your experience.</p>
            )}
            <button onClick={() => setShowReviewLookup(true)}
              className="tap-44 mt-5 inline-flex items-center gap-2 text-sm font-semibold text-navy-700
                border border-stone-200 px-4 py-2 rounded-full hover:border-amber-400 hover:text-amber-700 transition-colors">
              <Star size={14} className="fill-amber-500 text-amber-500" /> Write a Review
            </button>
          </div>

          {/* Review carousel */}
          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              {allReviews.slice(reviewIdx, reviewIdx + 3).map((r, i) => (
                <div
                  key={i}
                  className="relative bg-stone-50 rounded-2xl p-6 border border-stone-100"
                >
                  <Quote size={28} className="text-amber-300 mb-3" fill="currentColor" strokeWidth={0} />
                  <p className="text-sm text-navy-700 leading-relaxed mb-5">
                    {r.text}
                  </p>
                  <div className="flex items-center gap-3 pt-4 border-t border-stone-200">
                    <div
                      className="w-10 h-10 rounded-full bg-navy-100
                      flex items-center justify-center text-navy-700 font-bold text-xs"
                    >
                      {r.avatar}
                    </div>
                    <div>
                      <p className="font-semibold text-navy-900 text-sm">
                        {r.name}
                      </p>
                      <div className="flex items-center gap-1.5">
                        <StarRow n={r.rating} size={10} />
                        <span className="text-[10px] text-stone-400">
                          {r.date}
                        </span>
                      </div>
                    </div>
                    <span
                      className="ml-auto text-[10px] text-navy-400 font-semibold"
                    >
                      {r.via}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {/* Pagination */}
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: Math.ceil(allReviews.length / 3) }).map(
                (_, i) => (
                  <button
                    key={i}
                    onClick={() => setReviewIdx(i * 3)}
                    className={`h-1.5 rounded-full transition-all ${reviewIdx === i * 3 ? "w-8 bg-amber-500" : "w-1.5 bg-stone-200"}`}
                    aria-label={`Show reviews page ${i + 1}`}
                  />
                ),
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ════════ FAQ ════════ */}
      {/* Tenant-editable (Faq model, /api/faqs CRUD, managed from
          SettingsPage.jsx) — the section just doesn't render until an
          owner adds their own questions. */}
      {faqs && faqs.length > 0 && (
      <section id="faq" className="relative z-10 py-24 px-6 max-w-3xl mx-auto">
        <SectionEyebrow>Good To Know</SectionEyebrow>
        <h2 className="font-serif text-4xl font-medium text-navy-900 text-center mb-12">
          Frequently Asked Questions
        </h2>
        <div className="divide-y divide-stone-200 border-t border-b border-stone-200">
          {faqs.map((f, i) => (
            <div key={f._id || f.question}>
              <button
                onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 text-left py-5"
              >
                <span className="font-serif text-lg text-navy-900">{f.question}</span>
                <ChevronDown
                  size={18}
                  className={`text-navy-400 flex-shrink-0 transition-transform ${faqOpen === i ? "rotate-180" : ""}`}
                />
              </button>
              {faqOpen === i && (
                <p className="text-sm text-stone-500 leading-relaxed pb-5 pr-8">{f.answer}</p>
              )}
            </div>
          ))}
        </div>
      </section>
      )}

      {/* ════════ CONTACT + MAP ════════ */}
      <section
        id="contact"
        className="relative z-10 py-24 px-6 max-w-6xl mx-auto"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <SectionEyebrow align="left">Find Us</SectionEyebrow>
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-navy-900 mb-8">
              Location & Contact
            </h2>

            <div className="space-y-3 mb-6">
              {[
                {
                  href: `tel:${String(phone).replace(/\D/g, "")}`,
                  Icon: Phone,
                  label: phone,
                  sub: "Call or WhatsApp",
                },
                {
                  href: MAPS_URL,
                  Icon: MapPin,
                  label: address,
                  sub: "View on Google Maps",
                  ext: true,
                },
              ].map((c, i) => (
                <a
                  key={i}
                  href={c.href}
                  target={c.ext ? "_blank" : undefined}
                  rel={c.ext ? "noopener noreferrer" : undefined}
                  className="flex items-center gap-4 p-4 bg-white rounded-xl
                    border border-stone-200 hover:border-amber-300
                    transition-all group"
                >
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center bg-navy-50"
                  >
                    <c.Icon size={18} className="text-navy-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-navy-900 group-hover:text-amber-700 transition-colors">
                      {c.label}
                    </p>
                    <p className="text-xs text-stone-400">{c.sub}</p>
                  </div>
                  {c.ext && (
                    <ExternalLink
                      size={14}
                      className="ml-auto text-stone-300 group-hover:text-amber-500"
                    />
                  )}
                </a>
              ))}
            </div>

            <div className="flex gap-3">
              <a
                href="/book"
                className="flex-1 flex items-center justify-center gap-2 py-4 rounded-full
                  bg-navy-900 text-white font-bold text-sm
                  hover:bg-navy-800
                  transition-all"
              >
                Book Now <ArrowRight size={16} />
              </a>
              <a
                href={`https://wa.me/${String(phone).replace(/\D/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 py-4 rounded-full
                  border border-green-200 bg-green-50 text-green-700 font-semibold text-sm
                  hover:bg-green-100 transition-all"
              >
                💬 WhatsApp
              </a>
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden border border-stone-200">
            <iframe
              src={`https://www.google.com/maps?q=${
                mapsLat && mapsLng ? `${mapsLat},${mapsLng}` : encodeURIComponent(address || hotelName)
              }&output=embed`}
              width="100%"
              height="380"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={hotelName}
            />
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 py-3 bg-white border-t border-stone-100
                text-sm font-semibold text-navy-700 hover:text-amber-600 transition-colors"
            >
              <MapPin size={14} /> Open in Google Maps{" "}
              <ExternalLink size={12} />
            </a>
          </div>
        </div>
      </section>
      </main>

      {/* ════════ FOOTER ════════ */}
      <footer className="relative z-10 bg-navy-950 text-white py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between gap-10 mb-12">
            <div className="max-w-xs">
              <div className="flex items-center gap-2.5 mb-4">
                {logoUrl ? (
                  <img src={imgSrc(logoUrl)} alt={hotelName}
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div
                    className="w-10 h-10 rounded-full bg-white/10 border border-white/10
                    flex items-center justify-center"
                  >
                    <BedDouble size={18} className="text-amber-400" />
                  </div>
                )}
                <div>
                  <p className="font-serif font-medium text-lg leading-tight">
                    {hotelName}
                  </p>
                  <p className="text-[10px] text-navy-300 uppercase tracking-widest">
                    {address}
                  </p>
                </div>
              </div>
              <p className="text-navy-300 text-sm leading-relaxed">
                Your home away from home in the heart of {address}.
                Warm hospitality, modern comforts.
              </p>
              <div className="flex gap-2 mt-5">
                <a
                  href={`https://wa.me/${String(phone).replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tap-44 text-xs border border-white/10 text-navy-200 hover:border-amber-400/40 hover:text-amber-300
                    px-3 py-1.5 rounded-full transition-colors font-medium"
                >
                  WhatsApp
                </a>
                {crossPromoName && crossPromoUrl && (
                <a
                  href={crossPromoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tap-44 text-xs border border-white/10 text-navy-200 hover:border-amber-400/40 hover:text-amber-300
                    px-3 py-1.5 rounded-full transition-colors font-medium"
                >
                  {crossPromoName}
                </a>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-8 text-sm">
              <div>
                <p className="font-semibold text-white mb-3 uppercase text-xs tracking-wider">
                  Explore
                </p>
                {[
                  ["Rooms", "#rooms"],
                  ["Amenities", "#amenities"],
                  ["Gallery", "#gallery"],
                  ["Reviews", "#reviews"],
                  ["FAQ", "#faq"],
                ].map(([l, h]) => (
                  <a
                    key={l}
                    href={h}
                    className="block py-3 md:py-0 md:mb-2 text-navy-300 hover:text-amber-400 transition-colors"
                  >
                    {l}
                  </a>
                ))}
              </div>
              <div>
                <p className="font-semibold text-white mb-3 uppercase text-xs tracking-wider">
                  Contact
                </p>
                <p className="text-navy-300 mb-2">{phone}</p>
                <p className="text-navy-300 mb-2">
                  {address}
                </p>
                <a
                  href={MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tap-44 text-amber-400 hover:text-amber-300 text-xs inline-flex items-center gap-1"
                >
                  View on Maps <ArrowUpRight size={11} />
                </a>
              </div>
              <div>
                <p className="font-semibold text-white mb-3 uppercase text-xs tracking-wider">
                  Quick
                </p>
                <a
                  href="/book"
                  className="block py-3 md:py-0 md:mb-2 text-amber-400 font-semibold hover:text-amber-300"
                >
                  Book Now →
                </a>
                <a
                  href="/admin"
                  className="block py-3 md:py-0 md:mb-2 text-navy-300 hover:text-white"
                >
                  Admin Panel
                </a>
                {crossPromoName && crossPromoUrl && (
                <a
                  href={crossPromoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block py-3 md:py-0 text-navy-300 hover:text-amber-400"
                >
                  {crossPromoName}
                </a>
                )}
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-navy-400 text-xs">
              © {new Date().getFullYear()} {hotelName}, {address}. All rights reserved.
            </p>
            <p className="text-navy-400 text-xs">
              Built with ❤️ for great hospitality
            </p>
          </div>
        </div>
      </footer>

      {/* ════════ FLOATING STICKY BOOKING BAR (appears once the sidebar scrolls out of view) ════════ */}
      {/* lg+ only: the sidebar it replaces is itself lg-only, and below that breakpoint the
          mobile action bar already covers this bar's job — showing both stacks/overlaps them */}
      <div className={`hidden lg:block fixed bottom-6 inset-x-0 z-40 px-4 transition-all duration-500 transform ${
        showFloatingBar ? "translate-y-0 opacity-100" : "translate-y-16 opacity-0 pointer-events-none"
      }`}>
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl border border-stone-200 p-3 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="flex items-center gap-2 bg-stone-50 rounded-xl px-3 py-2 border border-stone-200">
              <span className="text-[11px] font-bold uppercase tracking-wider text-navy-500">In</span>
              <input
                type="date"
                value={checkIn}
                min={toLocalISODate(new Date())}
                onChange={(e) => setCheckIn(e.target.value)}
                className="bg-transparent text-xs font-semibold text-navy-900 focus:outline-none w-28 cursor-pointer"
              />
            </div>
            <div className="flex items-center gap-2 bg-stone-50 rounded-xl px-3 py-2 border border-stone-200">
              <span className="text-[11px] font-bold uppercase tracking-wider text-navy-500">Out</span>
              <input
                type="date"
                value={checkOut}
                min={checkIn || toLocalISODate(new Date())}
                onChange={(e) => setCheckOut(e.target.value)}
                className="bg-transparent text-xs font-semibold text-navy-900 focus:outline-none w-28 cursor-pointer"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <a
              href={bookHref}
              className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 text-xs font-bold text-white
                bg-navy-900 px-5 py-2.5 rounded-xl
                hover:bg-navy-800
                transition-all"
            >
              {nights > 0 ? `Reserve ${nights}N` : "Instant Booking"} <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </div>

      {/* Sticky bottom action bar (mobile) */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white
        border-t border-stone-200 safe-bottom px-4 py-3 flex items-center gap-3 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]">
        <a href={`tel:${String(phone).replace(/\D/g, "")}`}
          aria-label="Call us"
          className="w-12 h-12 flex-shrink-0 rounded-full border border-stone-200
            flex items-center justify-center text-navy-600">
          <Phone size={18} />
        </a>
        <a href="/book"
          className="flex-1 text-center text-sm font-bold text-white
            bg-navy-900 py-3.5 rounded-full">
          Book Direct
        </a>
      </div>
      <div className="h-16" />

      {/* Lightbox */}
      {galleryOpen !== null && (
        <div
          className="fixed inset-0 z-[100] bg-navy-950/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setGalleryOpen(null)}
        >
          <button className="absolute top-4 right-4 text-white/70 hover:text-white p-2">
            <X size={28} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setGalleryOpen(
                (galleryOpen - 1 + galleryItems.length) % galleryItems.length,
              );
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3
              border border-white/20 rounded-full"
          >
            <ChevronLeft size={24} />
          </button>
          <img
            src={galleryItems[galleryOpen].src}
            alt=""
            className="max-h-[85vh] max-w-full object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={(e) => {
              e.stopPropagation();
              setGalleryOpen((galleryOpen + 1) % galleryItems.length);
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3
              border border-white/20 rounded-full"
          >
            <ChevronRight size={24} />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
            {galleryOpen + 1} / {galleryItems.length}
          </div>
        </div>
      )}

      {showReviewLookup && (
        <ReviewLookupModal onClose={() => setShowReviewLookup(false)} />
      )}
    </div>
  );
}
